import { FlexLayout } from '@eduact/ed-system';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogProps,
	DialogTitle,
	Divider,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Submissions, SubmitAttemptQuestionPayload } from '../Types/Submissions.types';
import moment from 'moment';
import { Spacer } from '@Styled/utilities';
import AttemptQuestion, { AnswerSchema } from './AttemptQuestion';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from '@Providers/useSnackbar';
import { SubmissionsRequester } from '../Services/Submissions.requester';

type Props = {
	children?: any;
	submissionAttempt: Submissions;
	/** View only: inputs disabled, no Save / Submit. */
	readOnly?: boolean;
} & Omit<DialogProps, 'children'>;

const schema = object({
	answers: array(AnswerSchema),
});
const AttemptModal: React.VoidFunctionComponent<Props> = ({
	open,
	children,
	submissionAttempt,
	readOnly = false,
	...props
}) => {
	const { displaySnackbar } = useSnackbar();
	const closed = submissionAttempt.evaluation_status === 'closed';
	const { control, ...formProps } = useForm<{
		answers: Array<SubmitAttemptQuestionPayload>;
	}>({
		resolver: yupResolver(schema),
		defaultValues: {
			answers: submissionAttempt.answers.map((answer) => ({
				attempt_id: answer.test_attempt_id,
				question_id: answer.question.id,
				score: answer.score ?? undefined,
				weight: answer.question.weight,
				feedback: answer.feedback ?? '',
				submitted: answer.score !== null && answer.score !== undefined,
			})),
		},
	});

	const { fields, update } = useFieldArray({
		control,
		name: 'answers',
	});

	const {
		formState: { isValid },
		watch,
	} = formProps;
	// A grade typed but not saved (`submitted` null) never reached the server;
	// closing then would store NULL for it. Gate the close on every question
	// being saved, not merely valid. The list serves the attempt's SERVED essay
	// set (a placeholder for every essay left blank), so an unanswered essay
	// counts as unsaved here rather than vanishing from the total.
	const answers = watch('answers');
	const allSaved = (answers ?? []).every((answer) => answer.submitted === true);

	const handleSubmitAttempt = async () => {
		try {
			await SubmissionsRequester.getInstance().submitAttempt({
				attempt_id: submissionAttempt.id,
			});
			props.onClose?.({}, 'backdropClick');
		} catch (error: any) {
			// Closing is refused when a question's weight moved after it was
			// graded — the message names which ones to enter again. The modal
			// stays open so nothing is lost, and the failure is no longer
			// indistinguishable from a successful close.
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Could not close this attempt. Please try again.'
			);
		}
	};

	// The bulk verdict OVERWRITES every score and feedback and closes in one
	// request, so it always goes through the confirm dialog; only offered on
	// pending attempts (a finished grade must not be mass-wiped by a click).
	const [confirmVerdict, setConfirmVerdict] = useState<'full' | 'zero' | null>(null);
	const [bulkBusy, setBulkBusy] = useState(false);
	const handleBulkGrade = async (verdict: 'full' | 'zero') => {
		setBulkBusy(true);
		try {
			await SubmissionsRequester.getInstance().bulkGradeAttempt({
				attempt_id: submissionAttempt.id,
				verdict,
			});
			setConfirmVerdict(null);
			props.onClose?.({}, 'backdropClick');
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Could not close this attempt. Please try again.'
			);
		} finally {
			setBulkBusy(false);
		}
	};
	return (
		<Dialog open={open} {...props} fullWidth maxWidth="lg">
			<DialogWrapper>
				<FlexLayout mb={'1.25rem'} justifyContent={'space-between'}>
					<FlexLayout flexDirection={'column'}>
						<Typography fontWeight={600}>
							{submissionAttempt.user.username}
						</Typography>
						<Spacer mb="1.875rem" />
						<Typography color={'GrayText'}>
							{submissionAttempt.user.first_name}{' '}
							{submissionAttempt.user.last_name}
						</Typography>
					</FlexLayout>
					<FlexLayout flexDirection={'column'}>
						<Typography fontWeight={600}>
							{submissionAttempt.test.unit.course.name}
						</Typography>
						<Spacer mb="1.875rem" />
						<Typography color={'GrayText'}>
							{submissionAttempt.test.unit.name}
						</Typography>
					</FlexLayout>
					<FlexLayout flexDirection={'column'}>
						<Typography color={'GrayText'}>
							No. of Questions {submissionAttempt.answersCount}
						</Typography>
						<Spacer mb="1.875rem" />
						<Typography color={'GrayText'}>
							{moment(submissionAttempt.created_at).format('DD/MM/Y hh:mm A')}
						</Typography>
					</FlexLayout>
				</FlexLayout>
				<Divider />
				<Spacer mb="2.813rem" />
				<FormProvider control={control} {...formProps}>
					{fields.map((_, index) => {
						return (
							<AttemptQuestion
								// Keyed by the QUESTION: useFieldArray regenerates `field.id`
								// on every update(), and a placeholder row for an unanswered
								// essay has no row id yet.
								key={submissionAttempt.answers[index].question.id}
								index={index}
								answer={submissionAttempt.answers[index]}
								readOnly={readOnly}
								update={update}
							/>
						);
					})}
				</FormProvider>
			</DialogWrapper>
			{!readOnly && (
				<Footer>
					{/* Bulk verdicts skip the allSaved gate on purpose: they define
					    every score themselves. Zero left, full right. */}
					{!closed && (
						<Button
							onClick={() => setConfirmVerdict('zero')}
							disabled={bulkBusy}
							color="error"
							variant="contained"
						>
							ZERO SCORE &amp; SUBMIT
						</Button>
					)}
					<Button
						onClick={handleSubmitAttempt}
						disabled={!isValid || !allSaved}
						title={allSaved ? undefined : 'Save every question grade first'}
						variant="contained"
					>
						{closed ? 'RE-CLOSE ATTEMPT' : 'SUBMIT ATTEMPT'}
					</Button>
					{!closed && (
						<Button
							onClick={() => setConfirmVerdict('full')}
							disabled={bulkBusy}
							color="success"
							variant="contained"
						>
							FULL SCORE &amp; SUBMIT
						</Button>
					)}
				</Footer>
			)}
			<Dialog open={confirmVerdict !== null} onClose={() => setConfirmVerdict(null)}>
				<DialogTitle>
					{confirmVerdict === 'full' ? 'Full score & submit?' : 'Zero score & submit?'}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{confirmVerdict === 'full'
							? 'Every question gets its full score, with no feedback. '
							: 'Every question gets a zero, with no feedback. '}
						Existing scores and feedback are overwritten, the attempt is
						submitted, and the result becomes final for the student.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmVerdict(null)} disabled={bulkBusy}>
						Cancel
					</Button>
					<Button
						onClick={() => confirmVerdict && handleBulkGrade(confirmVerdict)}
						disabled={bulkBusy}
						color={confirmVerdict === 'full' ? 'success' : 'error'}
						variant="contained"
					>
						{confirmVerdict === 'full' ? 'Full score & submit' : 'Zero & submit'}
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

export default AttemptModal;

const DialogWrapper = styled.div`
	padding: 0 2.5rem;
	padding-top: 3.75rem;
	/* min-width: 60vw; */
	box-sizing: border-box;
	width: 100%;
`;

const Footer = styled.div`
	background: #f5f5f5;
	position: sticky;
	bottom: 0;
	box-shadow: 0px -3px 6px #00000029;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
	padding: 1.625rem;
`;
